import { GroupPeopleType, TextTypeEnum, VignetteTextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';

import { SpreadTypeEnum } from '../spread.dto';
import type { Person } from '@lite-workspace/dto-fotoclass-photo';

import type { VignetteKnobs, VignetteQuoteGroup } from '../project.dto';
import { resolveQuoteGroups, type VignetteQuoteGroupStates } from '../vignette-quote-groups';
import { isLumirOwnerCard, isPersonScopeGroup, OWNER_QUOTE_SCOPE, quoteScopeId } from './quote-scope.util';

type Node = Record<string, unknown>;

interface GroupParams {
  peopleType?: string | null;
  personId?: string | null;
  ownerCard?: boolean;
  packOwnerCard?: boolean;
}

/** Роль ячейки → переключатель цитат её группы; роль без переключателя (FREE) цитаты не гасит. */
const QUOTE_GROUP_OF_ROLE: Readonly<Record<string, VignetteQuoteGroup>> = {
  [GroupPeopleType.CLASS_TEACHER]: 'lead',
  [GroupPeopleType.TEACHER]: 'teachers',
  [GroupPeopleType.STUDENT]: 'students',
  [GroupPeopleType.OWNER]: 'owner',
  [GroupPeopleType.GROUP_WITH_OWNER]: 'owner',
};

const quoteGroupOf = (scope: Scope): VignetteQuoteGroup | undefined =>
  scope?.peopleType ? QUOTE_GROUP_OF_ROLE[scope.peopleType] : undefined;

/** Ближайшая персонная группа над рамкой — та же мерка, что у scope цитаты: FREE пропускается. */
type Scope = GroupParams | null;

const LEAD_SCOPE: Scope = { peopleType: GroupPeopleType.CLASS_TEACHER };

const quoteText = (box: Node): string => String((box['text'] as { text?: unknown } | undefined)?.text ?? '');

const isQuoteBox = (node: Node): boolean => node['type'] === 'text' && node['textType'] === TextTypeEnum.PERSON_QUOTE;

/**
 * Цитата руководителя «Как все» (R22), которую раскладка педагогов Люмира ставит под их полосу: id рамки —
 * `<ячейка или шаблон>-quote` (`leadTexts`). Её печатают и старые проекты — переключатель руководителя её не гасит.
 */
const isLumirLeadQuote = (node: Node): boolean => typeof node['id'] === 'string' && node['id'].endsWith('-quote');

/** Любая рамка цитаты руководителя: пака, «Как все» (R22), строка `leadLine`. */
const isLeadQuoteFrame = (node: Node): boolean => node['type'] === 'text' && node['textType'] === VignetteTextTypeEnum.LEAD_QUOTE;

/**
 * Отдельная цитата руководителя из пака: печатает его цитату, где бы ни лежала, и идёт за переключателем
 * руководителя. Рамки Люмира — строка (`leadLine`, на развороте только при включённой группе) и цитата «Как все»
 * под полосой педагогов — сюда не входят: проход их не трогает.
 */
const isPackLeadQuote = (node: Node): boolean => isLeadQuoteFrame(node) && node['leadLine'] !== true && !isLumirLeadQuote(node);

function blankQuote(box: Node): Node {
  return quoteText(box) === '' ? box : { ...box, text: { ...(box['text'] as Node), text: '' } };
}

/** Обход дерева рамок разворота: `blank` решает по рамке цитаты и её группе, остальное не трогается. */
function mapQuotes(nodes: unknown, scope: Scope, blank: (scope: Scope, box: Node) => boolean): unknown {
  if (!Array.isArray(nodes)) return nodes;
  let changed = false;
  const next = (nodes as Node[]).map(node => {
    let out = node;
    if (node['type'] === 'group') {
      const params = (node['params'] ?? {}) as GroupParams;
      const inner = isPersonScopeGroup(params) ? params : scope;
      const children = mapQuotes(node['drawableObjects'], inner, blank);
      if (children !== node['drawableObjects']) out = { ...node, drawableObjects: children };
    } else if ((isQuoteBox(node) && blank(scope, node)) || (isPackLeadQuote(node) && blank(LEAD_SCOPE, node))) {
      out = blankQuote(node);
    }

    if (out !== node) changed = true;
    return out;
  });
  return changed ? next : nodes;
}

function mapSpreadQuotes(spread: Node, blank: (scope: Scope, box: Node) => boolean): Node {
  const collages = spread['collages'];
  if (!Array.isArray(collages)) return spread;
  let changed = false;
  const next = (collages as Node[]).map(collage => {
    const objects = mapQuotes(collage['drawableObjects'], null, blank);
    if (objects === collage['drawableObjects']) return collage;
    changed = true;
    return { ...collage, drawableObjects: objects };
  });
  return changed ? { ...spread, collages: next } : spread;
}

function someQuote(nodes: unknown, scope: Scope, hit: (scope: Scope, box: Node) => boolean): boolean {
  if (!Array.isArray(nodes)) return false;
  return (nodes as Node[]).some(node => {
    if (node['type'] === 'group') {
      const params = (node['params'] ?? {}) as GroupParams;
      return someQuote(node['drawableObjects'], isPersonScopeGroup(params) ? params : scope, hit);
    }

    return hit(scope, node);
  });
}

/**
 * Переключатель, которому подчинена цитата ячейки. Карточка владельца Люмира (R413, R429) — обычная ячейка
 * ученика с `ownerCard`, но её цитата идёт за переключателем владельца, не учеников. Карточка пака — за
 * переключателем того, кто в ней сидит: владелец альбома — «владелец», ученик — «ученики»; мерка жильца та же,
 * что у лимитов цитат (`quoteScopeId`).
 */
function switchOf(scope: Scope, ownerPersonId?: string | null): VignetteQuoteGroup | undefined {
  if (scope?.ownerCard !== true || scope.peopleType !== GroupPeopleType.STUDENT) return quoteGroupOf(scope);
  if (isLumirOwnerCard(scope)) return 'owner';
  const ownersSeat = quoteScopeId({ groupBoxId: '', peopleType: scope.peopleType, personId: scope.personId }, ownerPersonId);
  return ownersSeat === OWNER_QUOTE_SCOPE ? 'owner' : 'students';
}

/**
 * Группа выключена — её цитаты не печатаются. Группа без нарисованных ячеек (`hidden`) считается
 * включённой: переключателя у неё нет, гасить нечего.
 */
function groupOff(resolved: VignetteQuoteGroupStates, scope: Scope, ownerPersonId?: string | null): boolean {
  const group = switchOf(scope, ownerPersonId);
  const state = group ? resolved[group] : undefined;
  return !!state && !state.on && !state.hidden;
}

/**
 * Цитаты виньетки по переключателям групп — на чтении, поверх подстановки: генератор текст цитаты не
 * хранит, поэтому гасит только этот проход. Рамка остаётся, печатает пустоту.
 *
 * 1. Цитата в ячейке выключенной группы → `''`.
 * 2. Альбом ученика (`ownerPersonId`): если на развороте уже печатается цитата в рамке владельца
 *    (OWNER / «группа со владельцем»), его собственная ячейка ученика цитату не повторяет —
 *    одна цитата человека на разворот. Эталон (без владельца) этот проход не делает.
 *
 * 3. Отдельная цитата руководителя пака (LEAD_QUOTE не из раскладки Люмира) идёт за переключателем руководителя.
 *
 * Серия Люмира таких рамок не несёт — для неё проход ничего не меняет.
 */
export function blankOffGroupQuotes<S extends Node>(
  spread: S,
  resolved: VignetteQuoteGroupStates | null | undefined,
  ownerPersonId?: string | null,
): S {
  if (!resolved || spread['spread_type'] !== SpreadTypeEnum.VIGNETTE) return spread;

  const offBlanked = mapSpreadQuotes(spread, scope => groupOff(resolved, scope, ownerPersonId));
  if (!ownerPersonId) return offBlanked as S;

  const ownerFrameQuote = (scope: Scope, box: Node): boolean =>
    isQuoteBox(box) && quoteGroupOf(scope) === 'owner' && quoteText(box).trim() !== '';
  const collages = (offBlanked['collages'] ?? []) as Node[];
  const ownerQuotePrinted = collages.some(collage => someQuote(collage['drawableObjects'], null, ownerFrameQuote));
  if (!ownerQuotePrinted) return offBlanked as S;

  return mapSpreadQuotes(
    offBlanked,
    scope => scope?.peopleType === GroupPeopleType.STUDENT && scope.personId === ownerPersonId,
  ) as S;
}

/**
 * Группы, чьи рамки цитат на развороте виньетки печатают непустой текст — та же мерка рамка → переключатель, что у
 * гашения. Без `ownerPersonId` (мастер-макет, эталон) жилец карточки пака — ученик.
 */
export function printedQuoteGroups(spread: Node, ownerPersonId?: string | null): ReadonlySet<VignetteQuoteGroup> {
  const printed = new Set<VignetteQuoteGroup>();
  if (spread['spread_type'] !== SpreadTypeEnum.VIGNETTE) return printed;

  mapSpreadQuotes(spread, (scope, box) => {
    const group = switchOf(scope, ownerPersonId);
    if (group && quoteText(box).trim() !== '') printed.add(group);
    return false;
  });
  // Руководитель — по любой его рамке, как в сверке раскладки на открытии: «Как все» и `leadLine` гашение обходит, но печатают.
  const collages = (spread['collages'] ?? []) as Node[];
  const leadPrinted = (_: Scope, box: Node): boolean => isLeadQuoteFrame(box) && quoteText(box).trim() !== '';
  if (collages.some(collage => someQuote(collage['drawableObjects'], null, leadPrinted))) printed.add('lead');
  return printed;
}

/**
 * Переключатели для подстановки на чтении — один вход для редактора, гейтвея и печати. Педагоги
 * считаются всегда: на виньетке «Только ученики» их ячеек нет, и состояние их групп ничего не гасит.
 */
export function vignetteQuoteGroupsFor(
  knobs: Partial<Pick<VignetteKnobs, 'quoteGroups'>> | null | undefined,
  persons: readonly Person[] | null | undefined,
): VignetteQuoteGroupStates {
  return resolveQuoteGroups(knobs, persons ?? [], { teachersOnVignette: true });
}
